
import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

export const Cursor: React.FC = () => {
  const cursorWrapperRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    // Detect touch capability to disable custom cursor on mobile
    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches || 
                          ('ontouchstart' in window) ||
                          navigator.maxTouchPoints > 0;
    
    if (!isTouchDevice) {
      setIsActive(true);
    }
  }, []);

  useEffect(() => {
    if (!isActive) return;

    // Hide default system cursor (redundant if handled by CSS, but good for safety)
    document.documentElement.style.cursor = 'none';
    document.body.style.cursor = 'none';

    // Initial positioning setup
    if (cursorWrapperRef.current) {
        gsap.set(cursorWrapperRef.current, { opacity: 0 });
    }
    if (ringRef.current) {
        gsap.set(ringRef.current, { xPercent: -50, yPercent: -50 });
    }
    if (dotRef.current) {
        gsap.set(dotRef.current, { xPercent: -50, yPercent: -50 });
    }

    const onMouseMove = (e: MouseEvent) => {
      // Reveal cursor on first interaction
      if (cursorWrapperRef.current) {
         gsap.to(cursorWrapperRef.current, { opacity: 1, duration: 0.4, overwrite: 'auto' });
      }

      // Outer ring follows with organic lag ("magnetic" feel)
      if (ringRef.current) {
          gsap.to(ringRef.current, {
            x: e.clientX,
            y: e.clientY,
            duration: 0.5,
            ease: "power3.out",
            overwrite: 'auto'
          });
      }

      // Inner dot follows instantly for precision
      if (dotRef.current) {
          gsap.set(dotRef.current, {
            x: e.clientX,
            y: e.clientY
          });
      }
    };

    const onMouseDown = () => setIsClicking(true);
    const onMouseUp = () => setIsClicking(false);

    const onMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // expanded interactive detection
      const interactive = target.closest('a, button, input, textarea, .cursor-pointer, [role="button"]');
      setIsHovering(!!interactive);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mouseover', onMouseOver);

    return () => {
      document.documentElement.style.cursor = 'auto';
      document.body.style.cursor = 'auto';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mouseover', onMouseOver);
    };
  }, [isActive]);

  // Handle Morphing States
  useEffect(() => {
    if (!isActive || !ringRef.current || !dotRef.current) return;

    const ring = ringRef.current;
    const dot = dotRef.current;

    if (isHovering) {
      // ACTIVE STATE: Tech Square Reticle
      gsap.to(ring, {
        width: 50,
        height: 50,
        borderRadius: "4px", // Slight rounded square
        rotation: 90,
        borderColor: "rgba(136, 136, 255, 0.6)", // Brand color #8888ff
        backgroundColor: "rgba(136, 136, 255, 0.03)",
        mixBlendMode: "normal",
        borderWidth: "1px",
        duration: 0.4,
        ease: "back.out(1.7)"
      });
      
      // Hide standard dot in favor of crosshairs
      gsap.to(dot, {
        scale: 0,
        opacity: 0,
        duration: 0.2
      });
    } else {
      // IDLE STATE: Discreet Circle
      gsap.to(ring, {
        width: 18,
        height: 18,
        borderRadius: "50%",
        rotation: 0,
        borderColor: "rgba(255, 255, 255, 0.3)",
        backgroundColor: "transparent",
        mixBlendMode: "difference", // Inverts over content
        borderWidth: "1px",
        duration: 0.4,
        ease: "power3.out"
      });
      
      gsap.to(dot, {
        scale: 1,
        opacity: 1,
        backgroundColor: "#ffffff",
        mixBlendMode: "difference",
        duration: 0.2
      });
    }
  }, [isHovering, isActive]);

  // Handle Click Impulse
  useEffect(() => {
    if (!isActive || !ringRef.current) return;
    if (isClicking) {
      gsap.to(ringRef.current, { scale: 0.8, duration: 0.1 });
    } else {
      gsap.to(ringRef.current, { scale: 1, duration: 0.3 });
    }
  }, [isClicking, isActive]);

  if (!isActive) return null;

  return (
    <div ref={cursorWrapperRef} className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden opacity-0">
      {/* Outer Targeting Ring */}
      <div 
        ref={ringRef} 
        className="absolute top-0 left-0 border flex items-center justify-center will-change-transform"
      >
        {/* Hover Crosshairs */}
        <div className={`absolute w-[140%] h-[1px] bg-[#8888ff] transition-opacity duration-300 ${isHovering ? 'opacity-40' : 'opacity-0'}`} />
        <div className={`absolute h-[140%] w-[1px] bg-[#8888ff] transition-opacity duration-300 ${isHovering ? 'opacity-40' : 'opacity-0'}`} />
        
        {/* Tech Corner Accents */}
        <div className={`absolute top-[-1px] left-[-1px] w-2 h-2 border-t-2 border-l-2 border-[#8888ff] transition-all duration-300 ${isHovering ? 'opacity-100' : 'opacity-0 translate-x-1 translate-y-1'}`} />
        <div className={`absolute top-[-1px] right-[-1px] w-2 h-2 border-t-2 border-r-2 border-[#8888ff] transition-all duration-300 ${isHovering ? 'opacity-100' : 'opacity-0 -translate-x-1 translate-y-1'}`} />
        <div className={`absolute bottom-[-1px] left-[-1px] w-2 h-2 border-b-2 border-l-2 border-[#8888ff] transition-all duration-300 ${isHovering ? 'opacity-100' : 'opacity-0 translate-x-1 -translate-y-1'}`} />
        <div className={`absolute bottom-[-1px] right-[-1px] w-2 h-2 border-b-2 border-r-2 border-[#8888ff] transition-all duration-300 ${isHovering ? 'opacity-100' : 'opacity-0 -translate-x-1 -translate-y-1'}`} />
      </div>

      {/* Center Precision Dot */}
      <div 
        ref={dotRef} 
        className="absolute top-0 left-0 w-1 h-1 bg-white rounded-full will-change-transform" 
      />
    </div>
  );
};
